import AmazonLogo from '../assets/logos/amazon.svg';
import AlibabaLogo from '../assets/logos/alibaba.svg';
import AliExpressLogo from '../assets/logos/aliexpress.svg';
import EbayLogo from '../assets/logos/ebay.svg';
import TemuLogo from '../assets/logos/temu.svg';
import SheinLogo from '../assets/logos/shein.svg';

const MARKETPLACE_CONFIG = {
  amazon: {
    name: 'Amazon',
    logo: AmazonLogo,
    bgColor: 'bg-[#FF9900]',
    textColor: 'text-white',
    tagline: "World's Largest Selection",
    brandColor: '#FF9900'
  },
  alibaba: {
    name: 'Alibaba',
    logo: AlibabaLogo,
    bgColor: 'bg-[#FF6A00]',
    textColor: 'text-white',
    tagline: 'Wholesale & Bulk Orders',
    brandColor: '#FF6A00'
  },
  aliexpress: {
    name: 'AliExpress',
    logo: AliExpressLogo,
    bgColor: 'bg-[#E62E04]',
    textColor: 'text-white',
    tagline: 'Unbeatable Prices',
    brandColor: '#E62E04'
  },
  ebay: {
    name: 'eBay',
    logo: EbayLogo,
    bgColor: 'bg-white',
    textColor: 'text-slate-900',
    tagline: 'Rare & Unique Finds',
    brandColor: '#0064D2'
  },
  temu: {
    name: 'TEMU',
    logo: TemuLogo,
    bgColor: 'bg-[#FB7701]',
    textColor: 'text-white',
    tagline: 'Shop Like a Billionaire',
    brandColor: '#FB7701'
  },
  shein: {
    name: 'SHEIN',
    logo: SheinLogo,
    bgColor: 'bg-black',
    textColor: 'text-white',
    tagline: 'Fashion Forward',
    brandColor: '#000000',
  },
  walmart: {
    name: 'Walmart',
    logo: null,
    bgColor: 'bg-[#0071CE]',
    textColor: 'text-white',
    tagline: 'Everyday Low Prices',
    brandColor: '#0071CE',
  },
  other: {
    name: 'Other',
    logo: null,
    bgColor: 'bg-slate-600',
    textColor: 'text-white',
    tagline: 'Custom marketplace',
    brandColor: '#475569',
  },
};

export function getMarketplaceConfig(marketplace) {
  const key = marketplace?.toLowerCase();
  return MARKETPLACE_CONFIG[key] || {
    name: marketplace || 'Unknown',
    logo: null,
    bgColor: 'bg-slate-600',
    textColor: 'text-white',
    tagline: '',
    brandColor: '#475569'
  };
}

export function MarketplaceLogo({ 
  marketplace, 
  size = 'md', 
  showName = false,
  className = '',
  inverted = false 
}) {
  const config = getMarketplaceConfig(marketplace);
  
  const sizeClasses = {
    xs: 'h-4',
    sm: 'h-5',
    md: 'h-6',
    lg: 'h-8',
    xl: 'h-10',
    '2xl': 'h-12'
  };
  
  const logoHeight = sizeClasses[size] || sizeClasses.md;
  
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      {config.logo ? (
        <img 
          src={config.logo} 
          alt={config.name}
          className={`${logoHeight} w-auto object-contain ${inverted ? 'brightness-0 invert' : ''}`}
          style={inverted ? {} : { color: config.brandColor }}
        />
      ) : (
        <span 
          className={`font-bold ${logoHeight === 'h-4' ? 'text-xs' : logoHeight === 'h-5' ? 'text-sm' : logoHeight === 'h-6' ? 'text-base' : logoHeight === 'h-8' ? 'text-lg' : 'text-xl'}`}
          style={{ color: inverted ? 'currentColor' : config.brandColor }}
        >
          {config.name}
        </span>
      )}
      {showName && <span className="font-medium">{config.name}</span>}
    </span>
  );
}

export function MarketplaceBadge({ 
  marketplace, 
  size = 'sm',
  className = '',
  showLabel = true,
}) {
  const config = getMarketplaceConfig(marketplace);
  
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-[10px]',
    sm: 'px-2.5 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1 rounded-full font-semibold shadow-sm max-w-full ${config.bgColor} ${config.textColor} ${sizeClasses[size] || sizeClasses.sm} ${className}`}
    >
      <MarketplaceLogo marketplace={marketplace} size="xs" inverted={config.bgColor !== 'bg-white'} />
      {showLabel ? <span className="uppercase truncate">{config.name}</span> : null}
    </span>
  );
}

export function MarketplaceCard({ 
  marketplace, 
  onClick,
  selected = false,
  className = '' 
}) {
  const config = getMarketplaceConfig(marketplace);
  
  return (
    <button
      onClick={onClick}
      className={`${config.bgColor} ${config.textColor} p-6 rounded-xl cursor-pointer transition-all hover:scale-105 hover:shadow-2xl group border ${
        config.bgColor === 'bg-white' ? 'border-slate-200' : 'border-transparent'
      } ${selected ? 'ring-4 ring-orange-400 ring-offset-2' : ''} ${className}`}
      data-testid={`marketplace-${marketplace}`}
    >
      <div className="h-12 mb-4 flex items-center justify-center">
        <MarketplaceLogo 
          marketplace={marketplace} 
          size="xl" 
          inverted={config.bgColor !== 'bg-white'} 
        />
      </div>
      <p className="text-sm opacity-80 text-center">{config.tagline}</p>
      <div className="mt-4 flex items-center justify-center gap-1 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        <span>Shop Now</span>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

export default MarketplaceLogo;
